import type { Footer, Page, Media } from "@/payload-types";
import type { PolymorphicRelationship } from "./url-utils";

/**
 * Footer transformation utilities
 * 
 * These utilities transform Payload CMS footer data into clean,
 * ready-to-use formats for our React components, following the same
 * pattern as navigation-utils.ts
 */

/**
 * Transform the entire footer global into a clean format
 * This is the main entry point - call this once in Footer component
 */
export function transformFooterData(footer: Footer | null) {
  if (!footer) return null;

  return {
    companyInfo: transformCompanyInfo(footer.companyInfo),
    navigationColumns: transformNavigationColumns(footer.navigationColumns),
    navigationColumnsRight: transformNavigationColumns(footer.navigationColumnsRight),
    contact: transformContact(footer.contact),
    accreditations: transformAccreditations(footer.accreditations),
    legalLinks: transformLegalLinks(footer.legalLinks),
    copyright: footer.copyright,
    websiteCredit: footer.websiteCredit,
  };
}

/**
 * Transform company info section
 */
function transformCompanyInfo(info: Footer["companyInfo"]) {
  if (!info) return undefined;

  return {
    logo: info.logo,
    brandName: info.brandName,
    tagline: info.tagline,
  };
}

/**
 * Transform navigation columns for footer menu
 */
function transformNavigationColumns(columns: Footer["navigationColumns"]) {
  if (!columns) return [];

  return columns.map(column => ({
    title: column.title,
    titleLink: column.titleLink,
    links: column.links?.map(link => ({
      id: link.id,
      label: link.label,
      linkType: link.linkType,
      page: link.page,
      url: link.url,
    })) || [],
  }));
}

/**
 * Transform contact information
 */
function transformContact(contact: Footer["contact"]) {
  if (!contact) return undefined;

  return {
    phoneNumber: contact.phoneNumber,
    phoneLabel: contact.phoneLabel,
    customerLoginUrl: contact.customerLoginUrl,
    customerLoginLabel: contact.customerLoginLabel,
  };
}

/**
 * Transform accreditations
 */
function transformAccreditations(accreditations: Footer["accreditations"]) {
  if (!accreditations || accreditations.length === 0) return undefined;

  return accreditations.map(item => ({
    id: item.id,
    type: item.type,
    name: item.name,
    badge: item.badge,
    widgetUrl: item.widgetUrl,
    url: item.url,
    width: item.width,
  }));
}

/**
 * Transform legal links
 */
function transformLegalLinks(links: Footer["legalLinks"]) {
  if (!links || links.length === 0) return undefined;

  return links.map(link => ({
    id: link.id,
    label: link.label,
    page: link.page,
  }));
}

/**
 * Type definitions for transformed data
 */

export interface SocialMediaLink {
  platform: 'facebook' | 'instagram' | 'linkedin';
  url: string;
}

export interface FooterLink {
  id?: string | null;
  label: string;
  linkType: "page" | "custom";
  page?: Page | PolymorphicRelationship | string | number | null;
  url?: string | null;
}

export interface FooterLegalLink {
  id?: string | null;
  label: string;
  page?: Page | PolymorphicRelationship | string | number | null;
}

export interface TransformedFooterData {
  companyInfo?: {
    logo?: Media | string | number | null;
    brandName?: string | null;
    tagline?: string | null;
  };
  navigationColumns: Array<{
    title?: string | null;
    titleLink?: {
      type?: ("none" | "page" | "custom") | null;
      page?: Page | PolymorphicRelationship | string | number | null;
      url?: string | null;
      newTab?: boolean | null;
    } | null;
    links: FooterLink[];
  }>;
  navigationColumnsRight: Array<{
    title?: string | null;
    titleLink?: {
      type?: ("none" | "page" | "custom") | null;
      page?: Page | PolymorphicRelationship | string | number | null;
      url?: string | null;
      newTab?: boolean | null;
    } | null;
    links: FooterLink[];
  }>;
  contact?: {
    phoneNumber?: string | null;
    phoneLabel?: string | null;
    customerLoginUrl?: string | null;
    customerLoginLabel?: string | null;
  };
  accreditations?: Array<{
    id?: string | null;
    type?: ("image" | "widget") | null;
    name: string;
    badge?: Media | string | number | null;
    widgetUrl?: string | null;
    url?: string | null;
    width?: number | null;
  }>;
  legalLinks?: FooterLegalLink[];
  copyright?: string | null;
  websiteCredit?: Footer["websiteCredit"];
}
